"use client";

import { TimeFrame } from "charts/src/types/chart";
import Image from "next/image";
import { useState } from "react";

import { ServiceQuotaMask } from "@/components/service-quota/ServiceQuotaMask";
import ChartTimeFrameButtonGroup from "@/features/analysis/components/key-metrices-charts/components/ChartTimeFrameButtonGroup";
import { useDialog } from "@/hooks/use-dialog";
import { Lang } from "@/i18n-config";
import { getTranslations } from "@/utils/i18n/get-translations";

type Props = {
  lang: Lang;
  symbol: string;
};

const DIALOG_WIDTH = 800;

const getChartConfigs = (lang: Lang, symbol: string) => {
  const { t } = getTranslations(lang);
  return [
    {
      chartId: "revenue-breakdown-chart",
      title: t.analysis.chartConfig.overview.revenueBreakdown(symbol),
      icon: "/chart/bar-blue.svg",
      chart: "/analysis/mock/revenue-breakdown-chart.png",
      timeframe: ["Q", "4Q", "Y"],
    },
    {
      chartId: "net-income-chart",
      title: t.analysis.chartConfig.overview.netIncome(symbol),
      icon: "/chart/line-blue.svg",
      chart: "/analysis/mock/net-income-chart.png",
      timeframe: ["Q", "4Q", "Y"],
    },
    {
      chartId: "cash-flow-chart",
      title: t.analysis.chartConfig.overview.cashFlow(symbol),
      icon: "/chart/bar-solid-blue.svg",
      chart: "/analysis/mock/cash-flow-chart.png",
      timeframe: ["Q", "Y"],
    },
    {
      chartId: "profit-margin-chart",
      title: t.analysis.chartConfig.overview.profitMargin(symbol),
      icon: "/chart/line-solid-blue.svg",
      chart: "/analysis/mock/profit-margin-chart.png",
      timeframe: ["Q", "4Q", "Y"],
    },
    {
      chartId: "pe-stream-chart",
      title: t.analysis.chartConfig.overview.peStream(symbol),
      icon: "/chart/line-solid-blue.svg",
      chart: "/analysis/mock/pe-stream-chart.png",
      timeframe: ["Q"],
    },
  ];
};

export const MockKeyMetricesView = ({ lang, symbol }: Props) => {
  const { t } = getTranslations(lang);
  const [timeFrame, setTimeFrame] = useState<TimeFrame>("Q");
  const chartConfigs = getChartConfigs(lang, symbol);
  const filteredConfig = chartConfigs.filter(({ timeframe: timeframeOption }) =>
    timeframeOption.includes(timeFrame)
  );

  return (
    <>
      <h2 className="mb-6 sm:mb-2">
        {t.analysis.overview.keyMetrices(symbol)}
      </h2>
      <div>
        <ChartTimeFrameButtonGroup
          lang={lang}
          type="overview"
          onButtonClick={(value) => setTimeFrame(value as TimeFrame)}
        />
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
          {filteredConfig.map(({ chartId, title, icon, chart }) => (
            <ChartGridItem
              key={chartId}
              title={title}
              icon={icon}
              chart={chart}
            />
          ))}
        </div>
      </div>
    </>
  );
};

const ChartGridItem = ({
  title,
  icon,
  chart,
}: {
  title: string;
  icon: string;
  chart: string;
}) => {
  const { Dialog, openDialog } = useDialog({
    isMobileFullScreen: false,
    zIndex: 100,
  });

  return (
    <>
      <Dialog width={DIALOG_WIDTH}>
        <ChartDialog
          title={title}
          icon={icon}
          chart={
            <ServiceQuotaMask>
              <Image
                src={chart}
                alt={title}
                width={340}
                height={230}
                className="w-full"
              />
            </ServiceQuotaMask>
          }
        />
      </Dialog>
      <div className="rounded-md bg-white p-5 shadow-[0_2px_6px_rgba(0,0,0,0.15)]">
        <div className="mb-4 flex items-center justify-between">
          <span className="flex items-center gap-2">
            <Image src={icon} alt={title} width={24} height={12} />
            <h3 className="font-semibold text-gray-700">{title}</h3>
          </span>
          <div className="hidden cursor-pointer sm:block" onClick={openDialog}>
            <Image
              src="/icons/expand.svg"
              width={16}
              height={16}
              alt="expand"
            />
          </div>
        </div>
        <ServiceQuotaMask>
          <Image
            src={chart}
            alt={title}
            width={340}
            height={230}
            className="w-full"
          />
        </ServiceQuotaMask>
      </div>
    </>
  );
};

const ChartDialog = ({
  title,
  icon,
  chart,
}: {
  title: string;
  icon: string;
  chart: React.ReactNode;
}) => {
  return (
    <div className="px-10">
      <h3 className="mb-4 flex items-center justify-center gap-2 text-2xl font-semibold text-gray-700">
        <Image src={icon} alt={title} width={24} height={24} />
        {title}
      </h3>
      {chart}
    </div>
  );
};
